import { EXTRA_FIELD_VALUE_TYPES } from '@/components/ExtraFields/types';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';

/**
 * Utility function to update extra field values on any entity
 * This function encapsulates the common logic for updating extra fields
 * across different entity types (leads, service requests, branches, branch services)
 *
 * @param entity - The entity object to update (lead, service request, branch, etc.)
 * @param extraFieldTypeId - The UUID of the extra field type
 * @param value - The new value(s) for the extra field
 * @param fieldType - The type of the field (TEXT, SELECT, MULTI_SELECT, etc.)
 * @returns A new entity object with the updated extra field
 */
export function updateEntityExtraField<T extends { extra_fields?: any }>(
  entity: T,
  extraFieldTypeId: string,
  value: any,
  fieldType: string,
): T {
  const extraFields = entity.extra_fields || {};
  const extraField = extraFields[extraFieldTypeId] || { values: [] };

  // Determine the key to use based on field type
  const valueKey = [EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT, EXTRA_FIELD_VALUE_TYPES.SELECT].includes(fieldType)
    ? 'option_uuid'
    : 'value';

  // Create the new extra field with updated values
  const newExtraField = {
    ...extraField,
    values: !Array.isArray(value)
      ? [{ [valueKey]: value }]
      : value.map(val => typeof val === 'string' ? { [valueKey]: val } : val),
  };

  // Return the updated entity
  return {
    ...entity,
    extra_fields: {
      ...extraFields,
      [extraFieldTypeId]: newExtraField,
    },
  };
}
// Both backends clear a value only when it arrives as null; '' or [] fails their
// `required` rule and the old value silently survives. 0 and false must pass through.
export function normalizeExtraFieldRequestValue(value: any): any {
  const isEmpty = (v: any) => v === '' || v === null || v === undefined;

  if (isEmpty(value)) return null;
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmpty) ? null : value;

  return value;
}

// Auto-attached field types must show up on the data sheet even before they
// have a value, so agents don't have to attach each one manually.
export function mergeAutoAttachedExtraFields(
  attachedFields: ExtraFieldTypeModel[],
  allFieldTypes: ExtraFieldTypeModel[],
  relatedTo: 'customer' | 'result',
): ExtraFieldTypeModel[] {
  const attachedIds = new Set(attachedFields.map((field) => field.uuid));
  const autoAttachedFields = allFieldTypes.filter(
    (field) =>
      field.related_to === relatedTo && field.is_auto_attached && !attachedIds.has(field.uuid),
  );

  return [...attachedFields, ...autoAttachedFields];
}

export function updateExtraFieldsArray(
  extraFields: ExtraFieldTypeModel[],
  values: Object,
): ExtraFieldTypeModel[] {
  return extraFields.map(extraField => {
    if (values.hasOwnProperty(extraField.slug)){
      const valueKey = [EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT, EXTRA_FIELD_VALUE_TYPES.SELECT].includes(extraField.field_type)
        ? 'option_uuid'
        : 'value';
      const value = values[extraField.slug]
      return {
        ...extraField,
        values: !Array.isArray(value)
          ? [{ [valueKey]: value }]
          : value.map(val => typeof val === 'string' ? { [valueKey]: val } : val)
      }
    }
    return extraField
  })
}
